import { Observable, forkJoin, of, map, switchMap } from 'rxjs';
import { Order } from '../../shared/models/Order';
import { BoletaService, OrderBoletaStatus } from '../services/boleta.service';
import { OrderAdminKpisDto, OrderAdminQuery, OrderService } from '../services/order.service';
import { formatPeruYmd, peruYmdDaysAgo } from './peru-datetime';

export interface AdminExportBundle {
  rows: Order[];
  kpis: OrderAdminKpisDto;
  boletas: Record<number, OrderBoletaStatus>;
}

export function loadAdminExport(
  orders: OrderService,
  boletas: BoletaService,
  query: OrderAdminQuery
): Observable<AdminExportBundle> {
  return forkJoin({
    rows: orders.exportAdminOrders(query),
    kpis: orders.getAdminKpis(query)
  }).pipe(
    switchMap(({ rows, kpis }) => {
      const known = boletaMapFromOrders(rows);
      const missing = orderIdsWithoutBoletaFlag(rows);
      if (!missing.length) return of({ rows, kpis, boletas: known });
      return boletas.estadosOrdenes(missing).pipe(
        map(flags => {
          flags.forEach(flag => { known[flag.orderId] = flag; });
          return { rows, kpis, boletas: known };
        })
      );
    })
  );
}

export function isTerminalOrderStatus(status?: string | null): boolean {
  const value = (status || '').toUpperCase();
  return value === 'ENTREGADO' || value === 'CANCELADO';
}

export function historyBounds(range: string | null | undefined): { from?: string; to?: string } {
  if (!range || range === 'all') return {};
  const days = Number(range);
  if (!Number.isFinite(days) || days <= 0) return {};
  return { from: peruYmdDaysAgo(days), to: formatPeruYmd() };
}

export function mergeOrderRow(rows: Order[], updated: Order, remove: boolean): { rows: Order[]; removed: boolean } {
  if (!updated?.id) return { rows, removed: false };
  const index = rows.findIndex(row => row.id === updated.id);
  if (index < 0) return { rows, removed: false };
  const next = rows.slice();
  if (remove) {
    next.splice(index, 1);
    return { rows: next, removed: true };
  }
  next[index] = { ...rows[index], ...updated, id: updated.id };
  return { rows: next, removed: false };
}

export function boletaStatusFromOrder(order: Order): OrderBoletaStatus | null {
  if (order.tieneBoleta == null && order.boletaId == null && !order.numeroCompleto) return null;
  return {
    orderId: order.id,
    tieneBoleta: !!order.tieneBoleta || order.boletaId != null,
    boletaId: order.boletaId,
    numeroCompleto: order.numeroCompleto,
    envioCliente: order.envioCliente as OrderBoletaStatus['envioCliente']
  };
}

export function boletaMapFromOrders(orders: Order[]): Record<number, OrderBoletaStatus> {
  const map: Record<number, OrderBoletaStatus> = {};
  orders.forEach(order => {
    const status = boletaStatusFromOrder(order);
    if (status) map[order.id] = status;
  });
  return map;
}

export function orderIdsWithoutBoletaFlag(orders: Order[]): number[] {
  return orders.filter(order => boletaStatusFromOrder(order) == null).map(order => order.id);
}
